import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  SaveIcon, 
  UsersIcon, 
  AcademicCapIcon, 
  BriefcaseIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/outline';
import { getSdm, updateSdmData } from 'services/apiRoutes';

export default function CMSSdmPage() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    totalPenduduk: 0,
    jk: [
      { jenis: 'Laki-laki', jumlah: 0 },
      { jenis: 'Perempuan', jumlah: 0 }
    ],
    pendidikan: [],
    pekerjaan: []
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchData = async () => {
    try {
      const res = await getSdm(slug);
      if (res.data && res.data.data) {
        const d = res.data.data;
        setFormData({
          totalPenduduk: d.totalPenduduk || d.total_penduduk || 0,
          jk: d.jk || [
            { jenis: 'Laki-laki', jumlah: d.laki_laki || 0 },
            { jenis: 'Perempuan', jumlah: d.perempuan || 0 }
          ],
          pendidikan: d.pendidikan || [],
          pekerjaan: d.pekerjaan || []
        });
      }
    } catch (error) {
      console.error("error sdm", error);
      toast.error("Gagal memuat data SDM");
    } finally {
      setLoading(false);
    }
  };

  const handleJkChange = (index, value) => {
    const newJk = [...formData.jk];
    newJk[index].jumlah = value;
    
    // Auto-calculate total
    const total = newJk.reduce((acc, curr) => acc + (parseInt(curr.jumlah) || 0), 0);
    
    setFormData(prev => ({ 
      ...prev, 
      jk: newJk,
      totalPenduduk: total.toString()
    }));
  };

  const handleListItemChange = (type, index, field, value) => {
    const newList = [...formData[type]];
    newList[index][field] = value;
    setFormData(prev => ({ ...prev, [type]: newList }));
  };

  const addItem = (type) => {
    const newItem = type === 'pendidikan' 
      ? { namaPendidikan: "", jumlah: "0" }
      : { namaPekerjaan: "", jumlah: "0" };
    setFormData(prev => ({ ...prev, [type]: [...prev[type], newItem] }));
  };

  const removeItem = (type, index) => {
    const newList = formData[type].filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, [type]: newList }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateSdmData(slug, formData);
      if (res.error) {
        toast.error(res.message || "Gagal menyimpan data");
      } else {
        toast.success("Data SDM berhasil disimpan!");
        fetchData();
      }
    } catch (error) {
      console.error("error save sdm", error);
      toast.error("Terjadi kesalahan");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading data SDM...</div>;

  return (
    <div className="space-y-8 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight text-premium">Manajemen SDM & Demografi</h2>
          <p className="mt-1 text-sm text-gray-500">Kelola statistik penduduk, pendidikan, dan pekerjaan desa.</p>
        </div>
        <button 
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-bold rounded-2xl shadow-lg shadow-primary/20 text-white bg-primary hover:bg-primary/90 transition-all duration-300 transform hover:scale-105 disabled:opacity-50"
        >
          <SaveIcon className="w-5 h-5 mr-2" />
          {saving ? "Menyimpan..." : "Simpan Perubahan"}
        </button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        {/* Total & Gender Stats */}
        <div className="xl:col-span-1 space-y-6">
          <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm space-y-6">
            <div className="flex items-center space-x-3 text-primary">
              <UsersIcon className="w-6 h-6" />
              <h3 className="font-bold text-gray-900">Statistik Utama</h3>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Total Penduduk</label>
                  <input 
                    type="number"
                    readOnly
                    value={formData.totalPenduduk}
                    className="block w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-2xl text-2xl font-black text-primary focus:outline-none shadow-inner"
                  />
                  <p className="mt-2 text-xs text-gray-400 font-medium italic flex items-center">
                    <UsersIcon className="w-3 h-3 mr-1" />
                    * Otomatis dihitung dari total Laki-laki & Perempuan
                  </p>
                </div>

                {formData.jk.map((item, idx) => (
                  <div key={idx}>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 px-1">Jumlah {item.jenis}</label>
                    <div className="relative group">
                      <input 
                        type="number"
                        value={item.jumlah}
                        onChange={(e) => handleJkChange(idx, e.target.value)}
                        className="block w-full px-5 py-3.5 border-2 border-gray-100 bg-white rounded-2xl text-gray-700 font-bold focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all duration-300 outline-none hover:border-gray-200"
                      />
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-primary transition-colors">
                        <UsersIcon className="w-5 h-5" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Education & Occupation Lists */}
          <div className="xl:col-span-2 space-y-8">
            {/* Pendidikan */}
            <div className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm space-y-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-blue-50 rounded-2xl text-blue-500">
                    <AcademicCapIcon className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="font-extrabold text-gray-900 text-lg">Tingkat Pendidikan</h3>
                    <p className="text-xs text-gray-400 font-medium">Distribusi pendidikan warga</p>
                  </div>
                </div>
                <button 
                  onClick={() => addItem('pendidikan')}
                  className="p-2.5 bg-primary/5 text-primary hover:bg-primary hover:text-white rounded-xl transition-all duration-300 transform hover:rotate-90"
                >
                  <PlusIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                {formData.pendidikan.map((item, idx) => (
                  <div key={idx} className="flex items-center space-x-4 p-4 rounded-2xl bg-gray-50/30 border border-transparent hover:border-gray-100 hover:bg-white transition-all duration-300 group">
                    <div className="flex-1">
                      <input 
                        type="text"
                        placeholder="Nama Pendidikan (misal: Tamat SD)"
                        value={item.namaPendidikan}
                        onChange={(e) => handleListItemChange('pendidikan', idx, 'namaPendidikan', e.target.value)}
                        className="w-full px-4 py-2.5 bg-transparent border-b-2 border-gray-100 focus:border-primary focus:outline-none text-sm font-bold text-gray-700 transition-all"
                      />
                    </div>
                    <div className="w-32">
                      <input 
                        type="number"
                        placeholder="Jumlah"
                        value={item.jumlah}
                        onChange={(e) => handleListItemChange('pendidikan', idx, 'jumlah', e.target.value)}
                        className="w-full px-4 py-2.5 bg-white border border-gray-100 rounded-xl text-center text-sm font-black text-primary focus:border-primary focus:outline-none transition-all"
                      />
                    </div>
                    <button 
                      onClick={() => removeItem('pendidikan', idx)}
                      className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                ))}
                {formData.pendidikan.length === 0 && (
                  <div className="py-12 text-center border-2 border-dashed border-gray-100 rounded-[2rem]">
                    <AcademicCapIcon className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                    <p className="text-sm text-gray-400 font-medium italic">Klik tombol + untuk menambah data pendidikan</p>
                  </div>
                )}
              </div>
            </div>

            {/* Pekerjaan */}
            <div className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm space-y-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-orange-50 rounded-2xl text-orange-500">
                    <BriefcaseIcon className="w-7 h-7" />
                  </div>
                  <div>
                    <h3 className="font-extrabold text-gray-900 text-lg">Jenis Pekerjaan</h3>
                    <p className="text-xs text-gray-400 font-medium">Mata pencaharian warga</p>
                  </div>
                </div>
                <button 
                  onClick={() => addItem('pekerjaan')}
                  className="p-2.5 bg-primary/5 text-primary hover:bg-primary hover:text-white rounded-xl transition-all duration-300 transform hover:rotate-90"
                >
                  <PlusIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                {formData.pekerjaan.map((item, idx) => (
                  <div key={idx} className="flex items-center space-x-4 p-4 rounded-2xl bg-gray-50/30 border border-transparent hover:border-gray-100 hover:bg-white transition-all duration-300 group">
                    <div className="flex-1">
                      <input 
                        type="text"
                        placeholder="Nama Pekerjaan (misal: Petani)"
                        value={item.namaPekerjaan}
                        onChange={(e) => handleListItemChange('pekerjaan', idx, 'namaPekerjaan', e.target.value)}
                        className="w-full px-4 py-2.5 bg-transparent border-b-2 border-gray-100 focus:border-primary focus:outline-none text-sm font-bold text-gray-700 transition-all"
                      />
                    </div>
                    <div className="w-32">
                      <input 
                        type="number"
                        placeholder="Jumlah"
                        value={item.jumlah}
                        onChange={(e) => handleListItemChange('pekerjaan', idx, 'jumlah', e.target.value)}
                        className="w-full px-4 py-2.5 bg-white border border-gray-100 rounded-xl text-center text-sm font-black text-primary focus:border-primary focus:outline-none transition-all"
                      />
                    </div>
                    <button 
                      onClick={() => removeItem('pekerjaan', idx)}
                      className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                ))}
                {formData.pekerjaan.length === 0 && (
                  <div className="py-12 text-center border-2 border-dashed border-gray-100 rounded-[2rem]">
                    <BriefcaseIcon className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                    <p className="text-sm text-gray-400 font-medium italic">Klik tombol + untuk menambah data pekerjaan</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <style>{`
          .text-premium {
            background: linear-gradient(135deg, #0f172a 0%, #334155 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(15px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }
        `}</style>
    </div>
  );
}
